import { useState, useEffect, useCallback } from "react"
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from "chart.js"
import ChartDataLabels from "chartjs-plugin-datalabels"
import { Doughnut, Bar } from "react-chartjs-2"
import axios from "axios"
import getTimeZoneDateRange from "../../utils/getTimeZoneDateRange"

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartDataLabels
)

// =============================================================================
// DRILL-DOWN MODAL COMPONENT
// =============================================================================
const DrillDownModal = ({ isOpen, onClose, stepData, users, loading, pagination, onPageChange, onSessionClick }) => {
  if (!isOpen) return null

  const showMyraColumn = stepData?.myraTouch === "landing" || stepData?.myraTouch === "post_plan"
  const statusColors = {
  "Signed up": "bg-blue-100 text-blue-700",
  "Completed GSA": "bg-green-100 text-green-700",
  "Started GSA": "bg-yellow-100 text-yellow-700",
  "LP Chat": "bg-purple-100 text-purple-700",
  "Blank": "bg-gray-100 text-gray-700",
};

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">
              Users at: <span className="text-teal-600">{stepData?.name}</span>
            </h2>     
            <p className="text-sm text-slate-500 mt-0.5">
              {stepData?.count?.toLocaleString()} users
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 text-3xl leading-none"
          >
            &times;
          </button>
        </div>

        {/* Table */}
        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
          {loading ? (
            <div className="text-center py-10 text-slate-500">Loading users...</div>
          ) : (
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Session ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Location
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Device
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Time on Step
                  </th>
                  {stepData.name ==='Landed on StartFreePT' && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Date / Time
                  </th>
                  )}
                  {showMyraColumn && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                      Myra Chat
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={showMyraColumn ? 6 : 5} className="text-center py-10 text-slate-500">
                      No users found at this step.
                    </td>
                  </tr>
                ) : (
                  users.map((user, index) => (
                    <tr 
                    key={user.session_id || index} 
                    className="hover:bg-slate-50"
                    onClick={() => {
                      if (stepData.name === "Landed on StartFreePT") {
                        onSessionClick(user.session_id); 
                      }
                    }}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-slate-600">
                        {user.session_id?.substring(0, 13) || "—"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                        {user.city && user.country
                          ? `${user.city}, ${user.country}`
                          : <span className="italic text-slate-400">Unknown</span>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                        {user.device_type
                          ? user.device_type.charAt(0).toUpperCase() + user.device_type.slice(1)
                          : "Unknown"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                        {formatDuration(user.time_on_step_sec)}
                      </td>
                      {stepData.name ==='Landed on StartFreePT' && (
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                        {formatDateTime(user.session_start_time)}
                      </td>
                      )}
                      {showMyraColumn && (
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {user.myra_engaged ? (
                            <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">
                              Yes
                            </span>
                          ) : (
                            <span className="text-slate-400">No</span>
                          )}
                        </td>
                      )}
                      {stepData.name ==='Landed on StartFreePT' ?(
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {user.status ? (
                          <span className={`px-2 py-0.5 text-xs font-semibold rounded-full  ${statusColors[user.status]}`}>
                            {user.status}
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">
                            Blank
                          </span>
                        )}
                      </td>
                      ):
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {user.exit_reason ? (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700">
                            {formatExitReason(user.exit_reason)}
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700">
                            Completed
                          </span>
                        )}
                      </td>
                      }                   
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination Footer */}
        <div className="p-4 border-t bg-slate-50 flex justify-between items-center">
          <button
            onClick={() => onPageChange(pagination.page - 1)}
            disabled={pagination.page <= 1}
            className="text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ← Previous {pagination.limit}
          </button>
          <span className="text-sm text-slate-500">
            Showing {((pagination.page - 1) * pagination.limit) + 1}-
            {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
          </span>
          <button
            onClick={() => onPageChange(pagination.page + 1)}
            disabled={pagination.page * pagination.limit >= pagination.total}
            className="text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next {pagination.limit} →
          </button>
        </div>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { width: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #64748b; }
      `}</style>
    </div>
  )
}

// =============================================================================
// SESSION DETAILS MODAL COMPONENT
// =============================================================================
const SessionDetailModal = ({ isOpen, onClose, sessionDetails, loading }) => {
  if (!isOpen) return null;

  // Process session details - the API returns an array of rows (one per chat message)
  // We need to extract journey info from the first row and group chats
  const journeyData = Array.isArray(sessionDetails) ? sessionDetails[0] : sessionDetails;
  
  // Helper to calculate status
  const getStatus = (j) => {
    if (!j) return { text: '', badge: '' };
    if (j.signed_up) return { text: 'Signed Up', badge: 'bg-green-100 text-green-700' };
    if (j.recovery_score_viewed || j.viewed_plan) return { text: 'Completed GSA', badge: 'bg-blue-100 text-blue-700' };
    if (j.started_assessment) {
      const steps = [
        { key: 'completed_safety_screen', label: 'Safety Screen' },
        { key: 'selected_problem_area', label: 'Problem Area' },
        { key: 'selected_pain_location', label: 'Pain Location' },
        { key: 'completed_nprs', label: 'NPRS' },
        { key: 'watched_demo_1', label: 'Demo Video' },
        { key: 'rom_test1_completed', label: 'Movement Test' },
      ];
      let lastCompleted = 'Started';
      for (const step of steps) {
        if (j[step.key]) lastCompleted = step.label;
        else break;
      }
      return { text: `Started GSA → ${lastCompleted}`, badge: 'bg-yellow-100 text-yellow-700' };
    }
    // Check if has chats
    if (Array.isArray(sessionDetails) && sessionDetails.some(r => r.chat_id)) {
      return { text: 'LP Chat', badge: 'bg-purple-100 text-purple-700' };
    }
    return { text: '', badge: '' };
  };

  const status = getStatus(journeyData);

  // Group chat messages by touchpoint, sorted chronologically (oldest first)
  const chatsByTouchpoint = {};
  const touchpointLabels = {
    landing: 'Landing Page',
    post_plan: 'Post-Assessment',
    post_session: 'In-App (Post-Session)'
  };
  const touchpointOrder = ['landing', 'post_plan', 'post_session'];

  if (Array.isArray(sessionDetails)) {
    sessionDetails
      .filter(row => row.chat_id)
      .sort((a, b) => new Date(a.chat_created_at) - new Date(b.chat_created_at))
      .forEach(row => {
        const tp = row.touchpoint || 'unknown';
        if (!chatsByTouchpoint[tp]) chatsByTouchpoint[tp] = [];
        chatsByTouchpoint[tp].push({
          id: row.chat_id,
          role: row.role,
          content: row.content,
          intent: row.intent,
          input_method: row.input_method,
          created_at: row.chat_created_at
        });
      });
  }

  // Journey milestone checks
  const milestones = journeyData ? [
    { label: 'Started GSA', completed: journeyData.started_assessment },
    { label: 'Safety Screen', completed: journeyData.completed_safety_screen },
    { label: 'Problem Area', completed: journeyData.selected_problem_area },
    { label: 'Pain Location', completed: journeyData.selected_pain_location },
    { label: 'NPRS/Symptoms', completed: journeyData.completed_nprs },
    { label: 'Demo Video', completed: journeyData.watched_demo_1 },
    { label: 'Movement Test 1', completed: journeyData.rom_test1_completed },
    { label: 'RPE', completed: journeyData.submitted_rpe_1 },
    { label: 'Symptom Response', completed: journeyData.submitted_symptoms_1 },
    { label: 'Demographics', completed: journeyData.completed_demographics },
    { label: 'Recovery Score', completed: journeyData.recovery_score_viewed },
    { label: 'Viewed Plan', completed: journeyData.viewed_plan },
    { label: 'Signed Up', completed: journeyData.signed_up },
  ] : [];

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center bg-gradient-to-r from-teal-50 to-white">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Session Details</h2>
            <p className="text-xs text-slate-500 mt-0.5 font-mono">
              {journeyData?.session_id}
            </p>
          </div>
          <button onClick={onClose} className="text-2xl text-gray-500 hover:text-gray-700">&times;</button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
          {loading ? (
            <div className="text-center text-gray-500 py-10">Loading session details...</div>
          ) : !journeyData ? (
            <div className="text-center text-gray-500 py-10">No details found.</div>
          ) : (
            <div className="space-y-6">
              {/* Device & Location Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 uppercase font-medium">Device</p>
                  <p className="text-sm font-semibold text-slate-800 mt-1 capitalize">{journeyData.device_type || '—'}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 uppercase font-medium">Location</p>
                  <p className="text-sm font-semibold text-slate-800 mt-1">
                    {[journeyData.city, journeyData.country].filter(Boolean).join(', ') || '—'}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 uppercase font-medium">Time Initiated</p>
                  <p className="text-sm font-semibold text-slate-800 mt-1">
                    {journeyData.session_start_time ? new Date(journeyData.session_start_time).toLocaleString() : '—'}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 uppercase font-medium">Status</p>
                  {status.text ? (
                    <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-semibold rounded-full ${status.badge}`}>{status.text}</span>
                  ) : (
                    <p className="text-sm text-slate-400 mt-1">—</p>
                  )}
                  {journeyData.user_id && (
                    <p className="text-xs text-teal-600 mt-1">User ID: {journeyData.user_id}</p>
                  )}
                </div>
              </div>

              {/* Journey Progress */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Journey Progress</h3>
                <div className="flex flex-wrap gap-2">
                  {milestones.map((m, i) => (
                    <span 
                      key={i} 
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        m.completed ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-400'
                      }`}
                    >
                      {m.completed ? '✓ ' : ''}{m.label}
                    </span>
                  ))}
                </div>
              </div>

              {/* Myra Conversations */}
              {Object.keys(chatsByTouchpoint).length > 0 ? (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Myra Conversations</h3>
                  {touchpointOrder.filter(tp => chatsByTouchpoint[tp]).map(tp => {
                    const msgs = chatsByTouchpoint[tp];
                    const questionCount = msgs.filter(m => m.role === 'user').length;
                    return (
                      <div key={tp} className="mb-6">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                          <h4 className="text-xs font-semibold text-purple-700 uppercase">{touchpointLabels[tp]}</h4>
                          <span className="text-xs text-slate-400">{questionCount} question{questionCount !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="space-y-3 pl-4 border-l-2 border-purple-100">
                          {msgs.map((msg, idx) => (
                            <div key={idx} className={`rounded-lg p-3 ${msg.role === 'user' ? 'bg-purple-50 border border-purple-100' : 'bg-slate-50 border border-slate-100'}`}>
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-xs font-semibold ${msg.role === 'user' ? 'text-purple-700' : 'text-slate-600'}`}>
                                  {msg.role === 'user' ? '👤 User' : '🤖 Myra'}
                                </span>
                                {msg.input_method && msg.role === 'user' && (
                                  <span className="px-1.5 py-0.5 text-xs rounded bg-white text-slate-500">{msg.input_method}</span>
                                )}
                              </div>
                              <p className="text-sm text-slate-700">{msg.content}</p>
                              {msg.intent && msg.role === 'assistant' && (
                                <p className="text-xs text-slate-400 mt-1">Intent: {msg.intent}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-6 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">No Myra conversations for this session.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};


// =============================================================================
// HELPER FUNCTIONS
// =============================================================================
const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return "—"
  if (seconds < 60) return `${seconds}s`
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`
}

// Helper function to format ISO date-time string
const formatDateTime = (date) => {
  if (!date) return "";

  const dt = new Date(date);

  const year = dt.getFullYear();
  const month = String(dt.getMonth() + 1).padStart(2, "0"); 
  const day = String(dt.getDate()).padStart(2, "0");
  const hours = String(dt.getHours()).padStart(2, "0");
  const minutes = String(dt.getMinutes()).padStart(2, "0");
  const seconds = String(dt.getSeconds()).padStart(2, "0");

  return `${month}-${day}-${year}`;
};


const formatExitReason = (reason) => {
  const mapping = {
    camera_permission_denied: "Camera denied",
    ar_init_failed: "AR failed",
    user_skipped: "Skipped",
    timeout: "Timeout",
    browser_not_supported: "Browser unsupported",
    device_not_supported: "Device unsupported",
    error_unknown: "Error",
  }
  return mapping[reason] || reason
}

const formatUserStatus = (status) => {
  const mapping = {
    "Signed up": { bg: "bg-blue-100", text: "text-blue-700" },
    "Completed GSA": { bg: "bg-green-100", text: "text-green-700" },
    "Started GSA": { bg: "bg-yellow-100", text: "text-yellow-700" },
    "LP Chat": { bg: "bg-purple-100", text: "text-purple-700" },
    "Blank": { bg: "bg-gray-100", text: "text-gray-700" },
  };

  return mapping[status] || { bg: "bg-gray-100", text: "text-gray-700" };
};

// =============================================================================
// MAIN ANALYTICS PAGE COMPONENT
// =============================================================================
const AnalyticsPage = (props) => {
  const { refreshKey } = props
  const [dateRange, setDateRange] = useState("today")
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone; // get user timezone
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastUpdated, setLastUpdated] = useState("")
  const [funnelView, setFunnelView] = useState("simple") // "simple" or "detailed"

  // Drill-down modal state
  const [modalOpen, setModalOpen] = useState(false)
  const [modalStepData, setModalStepData] = useState(null)
  const [modalUsers, setModalUsers] = useState([])
  const [modalLoading, setModalLoading] = useState(false)
  const [modalPagination, setModalPagination] = useState({ page: 1, limit: 50, total: 0 })

  // Session Details modal state
  const [sessionDetailModalOpen, setSessionDetailModalOpen] = useState(false);
  const [selectedSessionDetails, setSelectedSessionDetails] = useState(null);
  const [sessionDetailsLoading, setSessionDetailsLoading] = useState(false);

  // chat states
  const [chatModalOpen, setChatModalOpen] = useState(false)
  const [chatTouchpoint, setChatTouchpoint] = useState(null) // 'landing' or 'post_plan'
  const [chatSessions, setChatSessions] = useState([])
  const [chatLoading, setChatLoading] = useState(false)
  const [chatDetailsLoading, setChatDetailsLoading] = useState(false)
  const [chatPagination, setChatPagination] = useState({ page: 1, limit: 50, total: 0 })
  // Chat Detail Modal State (for individual conversation view)
  const [chatDetailModalOpen, setChatDetailModalOpen] = useState(false)
  const [selectedChatSession, setSelectedChatSession] = useState(null)
  const [chatMessages, setChatMessages] = useState([])

  // clicked on refresh -> reacalling the api
    useEffect(() => {
    if (refreshKey > 0) {
      fetchDashboardData()
    }
  }, [refreshKey])


  // get start and end time to fetch data 

  const {startDate, endDate} = getTimeZoneDateRange(timezone, dateRange); 

  // Fetch session Details for landed on startFreePt 
  const fetchSessionDetails = async (sessionId) => {
  
    setSessionDetailsLoading(true);

  try {
    const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token;

    if (token) {
          axios.defaults.headers.common["Authorization"] = token
        }

        const response = await axios.get(
          `${window.ajaxLink}cloud_dashboard/analytics/drilldown/session_details?sessionId=${sessionId}&range=${dateRange}&start_date=${startDate}&end_date=${endDate}`
        )

        const result = response.data?.responseData
        console.log("sessionDetails", result)

    setSelectedSessionDetails(result|| null);
   
  } catch (err) {
    console.error("Failed to fetch session details:", err);
    setSelectedSessionDetails(null);
  } finally {
    setSessionDetailsLoading(false);
  }
};

  // helper funtion for session details 
  const handleSessionClick = (sessionId) => {
  setSessionDetailModalOpen(true);
  fetchSessionDetails(sessionId);
};


  // fetching chats
  // Fetch chat sessions list for a specific touchpoint
  const fetchChatSessions = useCallback(async (touchpoint, page = 1) => {
      setChatLoading(true)

      try {
        const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token
        if (token) {
          axios.defaults.headers.common["Authorization"] = token
        }

        const response = await axios.get(
          `${window.ajaxLink}cloud_dashboard/analytics/prechats?range=${dateRange}&page=${page}&limit=50&touchpoint=${touchpoint}&start_date=${startDate}&end_date=${endDate}`
        )

        const result = response.data?.responseData
        
        // Group chats by session_id to show unique sessions
        const sessionMap = new Map()
        result?.chats?.forEach(chat => {
          if (!sessionMap.has(chat.session_id)) {
            sessionMap.set(chat.session_id, {
              session_id: chat.session_id,
              conversation_id: chat.conversation_id,
              touchpoint: chat.touchpoint,
              created_at: chat.created_at,
              message_count: 1,
              location: chat.country ,
              device: chat.device_info
            })
          } else {
            sessionMap.get(chat.session_id).message_count++
          }
        })

        setChatSessions(Array.from(sessionMap.values()))
        setChatPagination({
          page: result?.page || 1,
          limit: result?.limit || 50,
          total: result?.total || 0,
        })
      } catch (err) {
        console.error("Failed to fetch chat sessions:", err)
        setChatSessions([])
      } finally {
        setChatLoading(false)
      }
  }, [dateRange])

  // handlers for chat:
  // Open chat modal for specific touchpoint
const openChatModal = (touchpoint) => {
  setChatTouchpoint(touchpoint)
  setChatModalOpen(true)
  fetchChatSessions(touchpoint, 1)
}

// Handle chat modal pagination
const handleChatPageChange = (newPage) => {
  if (newPage < 1) return
  fetchChatSessions(chatTouchpoint, newPage)
}

// Open detail view for a specific chat session
const openChatDetail = (sessionId) => {
  // Filter messages for this session from the full chats array
  const sessionMessages = chatSessions.find(s => s.session_id === sessionId)
  setSelectedChatSession(sessionMessages)
  setChatDetailModalOpen(true)
  
  // Fetch full conversation details
  fetchChatDetail(sessionId)
}

// Fetch full conversation for a session
const fetchChatDetail = async (sessionId) => {
  setChatDetailsLoading(true)
  try {
    const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token
    if (token) {
      axios.defaults.headers.common["Authorization"] = token
    }

    const response = await axios.get(
      `${window.ajaxLink}cloud_dashboard/analytics/prechats?range=${dateRange}&session_id=${sessionId}&touchpoint=${chatTouchpoint}&start_date=${startDate}&end_date=${endDate}`
    )

    const result = response.data?.responseData
    setChatMessages(result?.chats || [])
  } catch (err) {
    console.error("Failed to fetch chat details:", err)
    setChatMessages([])
  } finally {
    setChatDetailsLoading(false)
  }
}

// chat modal
// =============================================================================
// MYRA CHAT SESSIONS MODAL COMPONENT
// =============================================================================
const ChatSessionsModal = ({ isOpen, onClose, touchpoint, sessions, loading, pagination, onPageChange, onSessionClick }) => {
  if (!isOpen) return null

  const touchpointLabels = {
    landing: 'Landing Page',
    post_plan: 'Post-Assessment'
  }
  const touchpointLabel = touchpointLabels[touchpoint] || touchpoint

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center bg-gradient-to-r from-purple-50 to-white">
          <div>
            <h2 className="text-xl font-semibold text-purple-800">
              {/* Myra Conversations: <span className="text-purple-600">{touchpointLabel}</span> */}
              Myra Conversations:
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              {pagination.total?.toLocaleString()} total sessions
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 text-3xl leading-none"
          >
            &times;
          </button>
        </div>

        {/* Sessions List */}
        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
          {loading ? (
            <div className="text-center py-10 text-slate-500">Loading chat sessions...</div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-10 text-slate-500">No chat sessions found.</div>
          ) : (
            <div className="grid gap-4">
              {sessions.map((session, index) => (
                <div
                  key={session.session_id || index}
                  onClick={() => onSessionClick(session.session_id)}
                  className="border border-purple-100 rounded-lg p-4 hover:bg-purple-50 cursor-pointer transition-all hover:shadow-md"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-sm font-medium text-slate-700">
                          {session.session_id?.substring(0, 16)}...
                        </span>
                        <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">
                          {session.message_count} messages
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        Conversation ID: {session.conversation_id}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(session.created_at).toLocaleString()}
                      </p>
                      <p className="text-xs text-slate-500 mt-2">
                        Location: {session.location}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        Device: {session.device}
                      </p>
                    </div>
                    <div className="text-purple-600 text-xl">→</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination Footer */}
        <div className="p-4 border-t bg-slate-50 flex justify-between items-center">
          <button
            onClick={() => onPageChange(pagination.page - 1)}
            disabled={pagination.page <= 1}
            className="text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ← Previous
          </button>
          <span className="text-sm text-slate-500">
            Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}
          </span>
          <button
            onClick={() => onPageChange(pagination.page + 1)}
            disabled={pagination.page * pagination.limit >= pagination.total}
            className="text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  )
}
// =============================================================================
// CHAT DETAIL MODAL COMPONENT (shows full conversation)
// =============================================================================
const ChatDetailModal = ({ isOpen, onClose, session, loading, messages }) => {
  if (!isOpen) return null

  // Sort messages chronologically (oldest first) for natural reading flow
  const sortedMessages = [...messages].sort((a, b) => {
    const dateCompare = new Date(a.created_at) - new Date(b.created_at);
    if (dateCompare !== 0) return dateCompare;
    return (a.turn_number || 0) - (b.turn_number || 0);
  });

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[60]"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center bg-gradient-to-r from-purple-50 to-white">
          <div>
            <h2 className="text-lg font-semibold text-purple-800">Conversation Details</h2>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-xs text-slate-500">{session?.location || 'Unknown location'}</span>
              <span className="text-xs text-slate-400">•</span>
              <span className="text-xs text-slate-500">{session?.user_id ? `User ID: ${session.user_id}` : 'Not signed up'}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 text-3xl leading-none"
          >
            &times;
          </button>
        </div>

        {/* Chat Messages - Chronological order, oldest at top */}
        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar space-y-4">
          {loading ? (
            <div className="text-center py-10 text-slate-500">Loading messages...</div>
          ) : sortedMessages.length === 0 ? (
            <div className="text-center py-10 text-slate-500">No messages in this conversation.</div>
          ) : (
            sortedMessages.map((msg, index) => (
              <div
                key={msg.id || index}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[80%] ${msg.role === 'user' ? 'bg-purple-50 border border-purple-100' : 'bg-slate-50 border border-slate-100'} rounded-lg p-4`}>
                  {/* Message Header */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs font-semibold ${msg.role === 'user' ? 'text-purple-700' : 'text-slate-600'}`}>
                      {msg.role === 'user' ? '👤 User' : '🤖 Myra'}
                    </span>
                    {msg.input_method && msg.role === 'user' && (
                      <span className="px-1.5 py-0.5 text-xs rounded bg-white text-slate-500">
                        {msg.input_method}
                      </span>
                    )}
                  </div>

                  {/* Message Content */}
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{msg.content}</p>

                  {/* Intent Info - only for assistant */}
                  {msg.intent && msg.role === 'assistant' && (
                    <div className="mt-2 pt-2 border-t border-slate-200">
                      <span className="text-xs text-slate-500">
                        Intent: <span className="font-medium text-slate-700">{msg.intent}</span>
                        {msg.intent_confidence && (
                          <span className="ml-2 text-slate-400">
                            ({(msg.intent_confidence * 100).toFixed(0)}%)
                          </span>
                        )}
                      </span>
                    </div>
                  )}

                  {/* Timestamp */}
                  <p className="text-xs text-slate-400 mt-2">
                    {new Date(msg.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-slate-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

  // =============================================================================
  // DATA FETCHING
  // =============================================================================
  const fetchDashboardData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token
      if (token) {
        axios.defaults.headers.common["Authorization"] = token
      }

      const response = await axios.get(
        `${window.ajaxLink}cloud_dashboard/analytics?range=${dateRange}&start_date=${startDate}&end_date=${endDate}`
      )
      setData(response.data?.responseData)
      setLastUpdated(new Date().toLocaleString())
    } catch (err) {
      setError(err?.message || "An unknown error occurred")
    } finally {
      setLoading(false)
    }
  }, [dateRange])

  const fetchDrillDownUsers = useCallback(async (stepId, page = 1) => {
    setModalLoading(true)

    try {
      const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token
      if (token) {
        axios.defaults.headers.common["Authorization"] = token
      }

      const response = await axios.get(
        `${window.ajaxLink}cloud_dashboard/analytics/drilldown?step=${stepId}&range=${dateRange}&page=${page}&limit=50&start_date=${startDate}&end_date=${endDate}`
      )

      const result = response.data?.responseData
      setModalUsers(result?.users || [])
      setModalPagination({
        page: result?.page || 1,
        limit: result?.limit || 50,
        total: result?.total || 0,
      })
    } catch (err) {
      console.error("Failed to fetch drill-down users:", err)
      setModalUsers([])
    } finally {
      setModalLoading(false)
    }
  }, [dateRange])

  useEffect(() => {
    fetchDashboardData()
  }, [fetchDashboardData])

  // =============================================================================
  // DRILL-DOWN HANDLERS
  // =============================================================================
  const openDrillDown = (step) => {
    setModalStepData(step)
    setModalOpen(true)
    fetchDrillDownUsers(step.id, 1)
  }

  const handlePageChange = (newPage) => {
    if (newPage < 1) return
    fetchDrillDownUsers(modalStepData.id, newPage)
  }

  // =============================================================================
  // FUNNEL DATA
  // =============================================================================
  const getFunnelSteps = () => {
    if (!data) return []

    if (funnelView === "simple") {
      return [
        { id: "landed", name: "Landed on StartFreePT", count: data.kpis?.totalSessions || 0, section: "landing", myraTouch: "landing" },
        { id: "started", name: "Started GSA", count: data.funnelSteps?.find(s => s.id === "started")?.count || 0, section: "gsa", myraTouch: null },
        { id: "viewed_plan", name: "Viewed Recovery Plan", count: data.funnelSteps?.find(s => s.id === "viewed_plan")?.count || 0, section: "plan", myraTouch: "post_plan" },
        { id: "signed_up", name: "Signed Up", count: data.funnelSteps?.find(s => s.id === "signed_up")?.count || 0, section: "signup", myraTouch: null, highlight: true },
      ]
    }

    // Detailed view - all steps from API
    return data.funnelStepsDetailed || []
  }

  // =============================================================================
  // CHART DATA GENERATORS
  // =============================================================================
  const getCpgChartData = () => {
    if (!data?.charts?.cpg) return null
    const cpgTotal = data.charts.cpg.reduce((sum, item) => sum + item.count, 0)
    if (cpgTotal === 0) return null

    return {
      labels: data.charts.cpg.map((item) => item.cpg_classification.replace("LBP with ", "")),
      datasets: [{
        data: data.charts.cpg.map((item) => item.count),
        backgroundColor: ["#14b8a6", "#0891b2", "#f59e0b", "#6366f1"],
      }],
    }
  }

  const getCpgChartOptions = () => {
    const cpgTotal = data?.charts?.cpg?.reduce((sum, item) => sum + item.count, 0) || 0
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: "bottom", labels: { boxWidth: 12, font: { size: 11 } } },
        datalabels: {
          formatter: (value) => ((value * 100) / cpgTotal).toFixed(0) + "%",
          color: "#fff",
          font: { weight: "bold" },
        },
      },
    }
  }

  const getSymptomChartData = () => {
    if (!data?.charts?.symptom) return null
    const { improved, tolerable, worsened } = data.charts.symptom
    if (improved + tolerable + worsened === 0) return null

    return {
      labels: ["Improved", "Same", "Worsened"],
      datasets: [{
        label: "Users",
        data: [improved, tolerable, worsened],
        backgroundColor: ["#10b981", "#f59e0b", "#ef4444"],
      }],
    }
  }

  const getSymptomChartOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: { legend: { display: false }, datalabels: { display: false } },
  })

  const getDeviceChartData = () => {
    if (!data?.charts?.devices) return null
    const { mobile, desktop, tablet } = data.charts.devices
    if (mobile + desktop + tablet === 0) return null

    return {
      labels: ["Mobile", "Desktop", "Tablet"],
      datasets: [{
        data: [mobile, desktop, tablet],
        backgroundColor: ["#10b981", "#3b82f6", "#f97316"],
      }],
    }
  }

  const getDeviceChartOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom", labels: { boxWidth: 12, font: { size: 11 } } },
      datalabels: { display: false },
    },
  })

  const getPainZonesChartData = () => {
    if (!data?.charts?.painZones || data.charts.painZones.length === 0) return null
    return {
      labels: data.charts.painZones.map((item) => item.zone),
      datasets: [{
        label: "Times Selected",
        data: data.charts.painZones.map((item) => item.count),
        backgroundColor: "#0d9488",
      }],
    }
  }

  const getPainZonesChartOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, datalabels: { display: false } },
  })

  // =============================================================================
  // RENDER: FUNNEL
  // =============================================================================
  const renderFunnel = () => {
    const steps = getFunnelSteps()
    if (steps.length === 0 || steps[0].count === 0) {
      return <div className="text-center py-10 text-slate-500">No funnel data for this period.</div>
    }

    const maxCount = steps[0].count

    const sectionColors = {
      landing: "bg-blue-50 border-blue-200",
      gsa: "bg-slate-50 border-slate-200",
      test: "bg-teal-50 border-teal-200",
      personalize: "bg-amber-50 border-amber-200",
      score: "bg-purple-50 border-purple-200",
      plan: "bg-indigo-50 border-indigo-200",
      signup: "bg-green-50 border-green-200",
    }

    return (
      <div className="space-y-1 gap-4">
        {steps.map((step, i) => {
          const prevCount = i > 0 ? steps[i - 1].count : step.count
          const widthPercent = maxCount > 0 ? (step.count / maxCount) * 100 : 0
          const dropOff = i > 0 && prevCount > 0 ? (100 - (step.count / prevCount) * 100).toFixed(1) : 0
          const conversionFromStart = maxCount > 0 ? ((step.count / maxCount) * 100).toFixed(1) : 0
          const bgColor = sectionColors[step.section] || "bg-slate-50 border-slate-200"

          return (
            <div key={step.id} className="relative">
              {i > 0 && dropOff > 0 && (
                <div className="flex items-center justify-end pr-4 h-6">
                  <div className="text-xs text-red-500 font-medium">↓ {dropOff}% drop</div>
                </div>
              )}
              {i === 0 && <div className="h-2"></div>}
              <div
                onClick={() => openDrillDown(step)}
                className={`funnel-step ${bgColor} p-3 rounded-lg border cursor-pointer transition-all hover:translate-x-1 hover:shadow-md ${
                  step.highlight ? "ring-2 ring-teal-500" : ""
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-slate-700 text-sm">{step.name}</span>
                    {step.highlight && (
                      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700">
                        CONVERSION
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-slate-800">{step.count.toLocaleString()}</span>
                    <span className="text-xs text-slate-500 ml-2">{conversionFromStart}%</span>
                  </div>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-1.5 mt-2">
                  <div
                    className="bg-teal-500 h-1.5 rounded-full transition-all duration-500"
                    style={{ width: `${widthPercent}%` }}
                  ></div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  // =============================================================================
  // RENDER: SCREEN TIMING
  // =============================================================================
  const renderScreenTiming = () => {
    if (!data?.screenTiming) return null

    const maxTime = Math.max(...data.screenTiming.map((s) => s.avg_duration_sec))

    return (
      <div className="space-y-3">
        {data.screenTiming.map((screen) => {
          const widthPercent = maxTime > 0 ? (screen.avg_duration_sec / maxTime) * 100 : 0
          const isHighlight = screen.screen === "test1_ar" || screen.screen === "test1_demo"

          return (
            <div key={screen.screen}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-600">{screen.label}</span>
                <span className={`font-medium ${isHighlight ? "text-teal-600" : "text-slate-800"}`}>
                  {formatDuration(screen.avg_duration_sec)}
                </span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-500 ${
                    isHighlight ? "bg-teal-500" : "bg-slate-400"
                  }`}
                  style={{ width: `${widthPercent}%` }}
                ></div>
              </div>
            </div>
          )
        })}
        <p className="text-xs text-slate-400 mt-4 text-center">Highlighted = AR/video screens</p>
      </div>
    )
  }

  // =============================================================================
  // RENDER: COMPLETION LIKELIHOOD
  // =============================================================================
  const renderCompletionLikelihood = () => {
    if (!data?.completionLikelihood) return null

    return (
      <div className="space-y-2">
        {data.completionLikelihood.map((item) => (
          <div
            key={item.step}
            className={`flex justify-between items-center py-2 border-b border-slate-100 ${
              item.isKeyMilestone ? "bg-teal-50 -mx-2 px-2 rounded" : ""
            }`}
          >
            <span className={`text-sm ${item.isKeyMilestone ? "font-medium text-teal-700" : "text-slate-600"}`}>
              {item.label}
            </span>
            <span className={`font-semibold ${item.isKeyMilestone ? "font-bold text-teal-700" : "text-slate-700"}`}>
              {item.likelihood}%
            </span>
          </div>
        ))}
        <div className="mt-4 p-3 bg-teal-50 rounded-lg border border-teal-100">
          <p className="text-xs text-teal-700">
            <strong>Key Insight:</strong> Users who reach Demographics have{" "}
            {data.completionLikelihood.find((i) => i.isKeyMilestone)?.likelihood || 90}% completion rate.
            This is your "point of no return."
          </p>
        </div>
      </div>
    )
  }

  // =============================================================================
  // RENDER: MOVEMENT TEST ANALYSIS
  // =============================================================================
  const renderMovementTestAnalysis = () => {
    if (!data?.movementTests) return null

    const renderTestCard = (test, testNum) => {
      // const completionRate = test.started > 0 ? ((test.completed / test.started) * 100).toFixed(1) : 0
      const completionRate = test.started > 0 ? Math.min((test.completed / test.started) * 100, 100).toFixed(1) : 0

      return (
        <div className="border border-slate-200 rounded-lg p-4">
          <h3 className="font-medium text-slate-700 mb-3">
            Movement Test {testNum}
            {testNum === 2 && <span className="text-xs text-slate-400 ml-1">(conditional)</span>}
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">{testNum === 2 ? "Required" : "Started"}</span>
              <span className="font-semibold text-slate-800">{test.started?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Completed</span>
              <span className="font-semibold text-teal-600">{test.completed?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Dropped</span>
              <span className="font-semibold text-red-500">{test.dropped?.toLocaleString()}</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2 mt-2">
              <div className="bg-teal-500 h-2 rounded-full" style={{ width: `${completionRate}%` }}></div>
            </div>
            <p className="text-xs text-slate-500 text-right">{completionRate}% completion rate</p>
          </div>

          {/* Drop-off reasons */}
          {test.dropReasons && test.dropReasons.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <p className="text-xs font-medium text-slate-500 uppercase mb-2">Drop-off Reasons</p>
              <div className="space-y-1.5">
                {test.dropReasons.map((reason) => (
                  <div key={reason.reason} className="flex justify-between items-center text-sm">
                    <span className="text-slate-600">{formatExitReason(reason.reason)}</span>
                    <span
                      className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                        reason.reason === "user_skipped"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {reason.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {data.movementTests.test1 && renderTestCard(data.movementTests.test1, 1)}
        {data.movementTests.test2 && renderTestCard(data.movementTests.test2, 2)}
      </div>
    )
  }

  // =============================================================================
  // RENDER: MYRA IMPACT
  // =============================================================================
  const renderMyraImpact = () => {
    if (!data?.myraImpact) return null

    const { landing, postPlan } = data.myraImpact

    return (
      <div className="space-y-4">
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-purple-700">With Myra (Landing)</span>
            <span className="font-bold text-purple-800">{landing?.withMyra || 0}%</span>
          </div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-500">Without Myra</span>
            <span className="font-medium text-slate-600">{landing?.withoutMyra || 0}%</span>
          </div>
          <p className="text-xs text-purple-600 mt-1">
            +{((landing?.withMyra || 0) - (landing?.withoutMyra || 0)).toFixed(1)}% GSA start rate
          </p>
        </div>
        <div className="border-t border-purple-100 pt-4">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-purple-700">With Myra (Post-Plan)</span>
            <span className="font-bold text-purple-800">{postPlan?.withMyra || 0}%</span>
          </div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-500">Without Myra</span>
            <span className="font-medium text-slate-600">{postPlan?.withoutMyra || 0}%</span>
          </div>
          <p className="text-xs text-purple-600 mt-1">
            +{((postPlan?.withMyra || 0) - (postPlan?.withoutMyra || 0)).toFixed(1)}% signup rate
          </p>
        </div>
      </div>
    )
  }

  // =============================================================================
  // RENDER: ERROR STATE
  // =============================================================================
  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 text-center">
          <h2 className="text-xl font-bold text-red-600">Connection Error</h2>
          <p className="text-slate-600 mt-2">
            Could not load dashboard data. Please ensure the backend server is running and accessible.
          </p>
          <p className="text-sm text-slate-500 mt-4">
            <strong>Details:</strong> {error}
          </p>
          <button
            onClick={fetchDashboardData}
            className="mt-6 bg-teal-600 text-white font-semibold py-2 px-5 rounded-lg text-sm hover:bg-teal-700"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  // =============================================================================
  // RENDER: MAIN
  // =============================================================================
  return (
    <div className="space-y-6">
      {/* Filters Section */}
      <section className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <label htmlFor="date-range" className="text-sm font-medium text-slate-600 whitespace-nowrap">
            Date Range
          </label>
          <select
            id="date-range"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="rounded-lg border-slate-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm px-3 py-2 bg-white border"
          >
            <option value="today">Today</option>
            <option value="wtd">Week to Date</option>
            <option value="mtd">Month to Date</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="all">All Time</option>
          </select>
        </div>
        {lastUpdated && (
          <div className="text-xs text-slate-500 sm:text-sm sm:ml-auto">
            <span className="font-medium text-slate-600">Last Updated:</span> {lastUpdated}
          </div>
        )}
      </section>

      {loading ? (
        <div className="text-center py-12">
          <div className="text-slate-500">Loading dashboard data...</div>
        </div>
      ) : (
        data && (
          <>
            {/* KPI Cards - Row 1: Core Metrics */}
            <section className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              <button 
                onClick={() => openDrillDown({
                  id: "landed",
                  name: "Landed on StartFreePT",
                  count: data.kpis?.totalSessions || 0,
                  section: "landing"
                })}
                className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:shadow-md transition-shadow cursor-pointer text-left"
              >
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Landed</p>
                <p className="mt-1 text-2xl font-bold text-slate-800">
                  {data.kpis?.totalSessions?.toLocaleString() || 0}
                </p>
                <p className="text-xs text-slate-400 mt-1">Total visitors</p>
              </button>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Started GSA</p>
                <p className="mt-1 text-2xl font-bold text-slate-800">
                  {data.kpis?.startedGsa?.toLocaleString() || 0}
                </p>
                <p className="text-xs text-teal-600 mt-1">{data.kpis?.startRate || 0}% of landed</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Viewed Plan</p>
                <p className="mt-1 text-2xl font-bold text-slate-800">
                  {data.kpis?.viewedPlan?.toLocaleString() || 0}
                </p>
                <p className="text-xs text-teal-600 mt-1">{data.kpis?.completionRate || 0}% of started</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Signed Up</p>
                <p className="mt-1 text-2xl font-bold text-teal-600">
                  {data.kpis?.signedUp?.toLocaleString() || 0}
                </p>
                <p className="text-xs text-teal-600 mt-1">{data.kpis?.signUpRate || 0}% of viewed</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Overall Conversion</p>
                <p className="mt-1 text-2xl font-bold text-teal-600">{data.kpis?.overallConversion || 0}%</p>
                <p className="text-xs text-slate-400 mt-1">Land → Signup</p>
              </div>
            </section>

            {/* KPI Cards - Row 2: Myra & Timing Metrics */}
            <section className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {/* Myra Chat - Landing */}
              <button 
                onClick={() => openChatModal('landing')} 
                className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-4 shadow-sm border border-purple-100 hover:shadow-md transition-shadow cursor-pointer text-left"
              >
                <p className="text-xs font-medium text-purple-600 uppercase tracking-wide">Myra Chat (Landing)</p>
                <p className="mt-1 text-2xl font-bold text-purple-700">
                  {data.kpis?.myraLandingCount?.toLocaleString() || 0}
                </p>
                <p className="text-xs text-purple-500 mt-1">{data.kpis?.myraLandingRate || 0}% engaged</p>
              </button>

              {/* Myra Chat - Post-Plan */}
              <button 
                onClick={() => openChatModal('post_plan')} 
                className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-4 shadow-sm border border-purple-100 hover:shadow-md transition-shadow cursor-pointer text-left"
              >
                <p className="text-xs font-medium text-purple-600 uppercase tracking-wide">Myra Chat (Post-Plan)</p>
                <p className="mt-1 text-2xl font-bold text-purple-700">
                  {data.kpis?.myraPostPlanCount?.toLocaleString() || 0}
                </p>
                <p className="text-xs text-purple-500 mt-1">{data.kpis?.myraPostPlanRate || 0}% of viewers</p>
              </button>

              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Avg Time on Landing</p>
                <p className="mt-1 text-2xl font-bold text-slate-800">
                  {formatDuration(data.kpis?.avgTimeLanding)}
                </p>
                <p className="text-xs text-slate-400 mt-1">Before GSA start</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Avg GSA Duration</p>
                <p className="mt-1 text-2xl font-bold text-slate-800">
                  {formatDuration(data.kpis?.avgGsaDuration)}
                </p>
                <p className="text-xs text-slate-400 mt-1">Start to completion</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Test 1 Completion</p>
                <p className="mt-1 text-2xl font-bold text-slate-800">{data.kpis?.test1CompletionRate || 0}%</p>
                <p className="text-xs text-slate-400 mt-1">Of those who started</p>
              </div>
            </section>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column: Detailed Funnel (2/3 width) */}
              <div className="lg:col-span-2 space-y-6">
                {/* Detailed Journey Funnel */}
                <section className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-lg font-semibold text-slate-800">User Journey Funnel</h2>
                      <p className="text-xs text-slate-500 mt-0.5">Click any step to see individual users</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setFunnelView("simple")}
                        className={`text-xs px-3 py-1.5 rounded-lg font-medium ${
                          funnelView === "simple"
                            ? "bg-teal-100 text-teal-700"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      >
                        Simple
                      </button>
                      <button
                        onClick={() => setFunnelView("detailed")}
                        className={`text-xs px-3 py-1.5 rounded-lg font-medium ${
                          funnelView === "detailed"
                            ? "bg-teal-100 text-teal-700"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      >
                        Detailed
                      </button>
                    </div>
                  </div>
                  {renderFunnel()}
                </section>

                {/* Movement Test Analysis */}
                <section className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                  <h2 className="text-lg font-semibold text-slate-800 mb-4">Movement Test Analysis</h2>
                  {renderMovementTestAnalysis()}
                </section>
              </div>

              {/* Right Column: Insights (1/3 width) */}
              <div className="space-y-6">
                {/* Screen Time Analysis */}
                <section className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                  <h2 className="text-lg font-semibold text-slate-800 mb-4">Avg. Time Per Screen</h2>
                  {renderScreenTiming()}
                </section>

                {/* Point of No Return */}
                <section className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                  <h2 className="text-lg font-semibold text-slate-800 mb-2">Completion Likelihood</h2>
                  <p className="text-xs text-slate-500 mb-4">
                    If user reaches this step, what's the chance they complete GSA?
                  </p>
                  {renderCompletionLikelihood()}
                </section>

                {/* Myra Impact Preview */}
                <section className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-6 shadow-sm border border-purple-100">
                  <h2 className="text-lg font-semibold text-purple-800 mb-2">Myra Impact</h2>
                  <p className="text-xs text-purple-600 mb-4">Conversion comparison</p>
                  {renderMyraImpact()}
                </section>
              </div>
            </div>

            {/* Charts Row */}
            <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Clinical Profiles (CPG)</h3>
                <div className="h-64">
                  {getCpgChartData() ? (
                    <Doughnut data={getCpgChartData()} options={getCpgChartOptions()} />
                  ) : (
                    <div className="flex items-center justify-center h-full text-slate-500 font-medium">
                      No Data
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Symptom Response (Test 1)</h3>
                <div className="h-64">
                  {getSymptomChartData() ? (
                    <Bar data={getSymptomChartData()} options={getSymptomChartOptions()} />
                  ) : (
                    <div className="flex items-center justify-center h-full text-slate-500 font-medium">
                      No Data
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Device Distribution</h3>
                <div className="h-64">
                  {getDeviceChartData() ? (
                    <Doughnut data={getDeviceChartData()} options={getDeviceChartOptions()} />
                  ) : (
                    <div className="flex items-center justify-center h-full text-slate-500 font-medium">
                      No Data
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Pain Zones Chart */}
            <section className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Top Problem Areas Selected</h3>
              <div className="h-80">
                {getPainZonesChartData() ? (
                  <Bar data={getPainZonesChartData()} options={getPainZonesChartOptions()} />
                ) : (
                  <div className="flex items-center justify-center h-full text-slate-500 font-medium">
                    No Data
                  </div>
                )}
              </div>
            </section>
          </>
        )
      )}

      {/* Drill-Down Modal */}
      <DrillDownModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        stepData={modalStepData}
        users={modalUsers}
        loading={modalLoading}
        pagination={modalPagination}
        onPageChange={handlePageChange}
        onSessionClick = {handleSessionClick}
      />

      {/* Session-Details Modal */}
      <SessionDetailModal
        isOpen={sessionDetailModalOpen}
        onClose={() => setSessionDetailModalOpen(false)}
        sessionDetails={selectedSessionDetails}
        loading={sessionDetailsLoading}
      />

      {/* Chat Sessions Modal */}
      <ChatSessionsModal
        isOpen={chatModalOpen}
        onClose={() => setChatModalOpen(false)}
        touchpoint={chatTouchpoint}
        sessions={chatSessions}
        loading={chatLoading}
        pagination={chatPagination}
        onPageChange={handleChatPageChange}
        onSessionClick={openChatDetail}
      />

      {/* Chat Detail Modal */}
      <ChatDetailModal
        isOpen={chatDetailModalOpen}
        onClose={() => setChatDetailModalOpen(false)}
        session={selectedChatSession}
        loading={chatDetailsLoading}
        messages={chatMessages}
      />

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { width: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #64748b; }
      `}</style>
    </div>
  )
}

export default AnalyticsPage
